import { DoubleSectionSelectPill } from "PFComponents/double_section_pill/double_section_select_pill";
import { DropdownOption } from "PFComponents/dropdown/dropdown";
import { Experience, SkillImportance } from "PFTypes";
import { Id } from "PFTypes/id";
import React, { useCallback, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { SkillData, SkillsFrameworkData } from "../../../../skills_frameworks.types";
import css from "./selected_skills_box.module.scss";

type SelectedSkillsBoxProps = {
  skillImportance: SkillImportance;
};

export const SelectedSkillsBox = ({ skillImportance }: SelectedSkillsBoxProps): JSX.Element => {
  const subsectionName = skillImportance === SkillImportance.Essential ? "essential" : "supporting";

  const { t } = useTranslation("skillsFrameworks", { keyPrefix: "edit" });
  const { setValue } = useFormContext<SkillsFrameworkData>();
  const skills = useWatch<SkillsFrameworkData>({
    name: `skills.${subsectionName}`
  }) as SkillData[];

  const skillExperienceOptions: DropdownOption[] = useMemo(
    () => [
      {
        id: Experience.Basic,
        item: Experience.Basic,
        displayElement: t("basic")
      },
      {
        id: Experience.Intermediate,
        item: Experience.Intermediate,
        displayElement: t("intermediate")
      },
      {
        id: Experience.Advanced,
        item: Experience.Advanced,
        displayElement: t("advanced")
      }
    ],
    [t]
  );

  const importanceToTitleMap = useMemo<Record<SkillImportance, string>>(
    () => ({
      [SkillImportance.Essential]: t("essentialWithCount", { count: skills.length }),
      [SkillImportance.Supporting]: t("supportingWithCount", { count: skills.length })
    }),
    [t, skills.length]
  );

  const handleExperienceChange = useCallback(
    (skillId: Id, newExperience: Experience) => {
      setValue(
        `skills.${subsectionName}`,
        skills.map((skillData) =>
          skillData.skill.id === skillId ? { ...skillData, experience: newExperience } : skillData
        ),
        {
          shouldDirty: true
        }
      );
    },
    [skills, setValue, subsectionName]
  );

  const handleSkillDelete = useCallback(
    (skillId: Id) => {
      setValue(
        `skills.${subsectionName}`,
        skills.filter(({ skill }) => skill.id !== skillId),
        {
          shouldDirty: true
        }
      );
    },
    [skills, setValue, subsectionName]
  );

  return (
    <div className={css.skillsBox}>
      <h5 className={css.title}>{importanceToTitleMap[skillImportance]}</h5>
      {skills.length > 0 && (
        <div className={css.skillsList}>
          {skills.map(({ skill: { id, value }, experience }) => (
            <DoubleSectionSelectPill
              key={id}
              leftContent={String(value)}
              attribute={skillExperienceOptions.find(({ item }) => item === experience)?.displayElement || ""}
              attributeOptions={skillExperienceOptions}
              onAttributeChange={(newExperience: Experience) => handleExperienceChange(id, newExperience)}
              onDelete={() => handleSkillDelete(id)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
